// Leaving a lesson builder page (another stage, Back, the lesson overview, Exit, Sign out, the browser's Back or Forward,
// the login page once a session ends) while something on it is unsaved or under way: a dialog says what, and offers
// Stay here, Discard changes, and Save and continue when what is unsaved is the page's own edits, which its Save all
// saves. A save that fails, even in part, stays on the page with the edits it did not save. An open proposal review's
// own question (edits not kept, a save under way) is asked in the same dialog. Closing or reloading the browser tab
// gets the browser's own warning (each page's beforeunload).
//
// A link or button waits for the answer (the router's getUserConfirmation, below). The browser's Back or Forward has
// already moved when it is asked: it is refused at once, so the browser returns to this page while the dialog is open
// (pressing Back again asks nothing more), and goes on to where it was going once the teacher chooses to leave.
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import type { Action, History, Location } from 'history';
import React from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { isJwtLoggedIn } from '../util/session';
import { INTER, NAVY, SLATE } from '../style/verbaScholaStyles';

const DIALOG_MESSAGE = 'verbaschola-leave-dialog:';
const dialogs = new Map<string, (answer: (leave: boolean) => void) => void>();

/** The router's question for a Prompt: a builder page's leave dialog answers its own, later; any other question is the
 * browser's OK/Cancel, as by default. */
export function confirmNavigation(message: string, answer: (leave: boolean) => void) {
    const ask = message.startsWith(DIALOG_MESSAGE) ? dialogs.get(message.slice(DIALOG_MESSAGE.length)) : undefined;
    if (ask) ask(answer);
    else answer(window.confirm(message.startsWith(DIALOG_MESSAGE) ? 'You have unsaved changes on this page. Leave it?' : message));
}

// Where each history entry this tab has seen sits in the browser's history, by the router's location key, so a refused
// Back or Forward can be made again (history.go) once the teacher chooses to leave. The browser keeps its history
// across a reload, so the tab keeps these too (sessionStorage), read again after a reload or a Back or Forward into
// the app.
const ENTRIES_STORE = 'verbaschola.historyEntries';
let trackedHistory: History | null = null;
let entryKeys: (string | null)[] = [];
let entryIndex = 0;
// Calls of window.history.go: the history library's own return of the browser after a refused Back or Forward is one.
let goCalls = 0;
function storeEntries() {
    try { sessionStorage.setItem(ENTRIES_STORE, JSON.stringify({ keys: entryKeys, index: entryIndex })); } catch { /* kept for this load only */ }
}
function savedEntries(): unknown[] {
    const navigation = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (navigation?.type !== 'reload' && navigation?.type !== 'back_forward') return [];
    try {
        const stored: unknown = JSON.parse(sessionStorage.getItem(ENTRIES_STORE) ?? 'null');
        const keys = stored && typeof stored === 'object' ? (stored as { keys?: unknown }).keys : undefined;
        return Array.isArray(keys) && keys.every(entry => entry === null || typeof entry === 'string') ? keys : [];
    } catch {
        return [];
    }
}
function trackHistory(history: History) {
    if (trackedHistory === history) return;
    trackedHistory = history;
    const go = window.history.go.bind(window.history);
    window.history.go = (delta?: number) => { goCalls++; go(delta); };
    const key = history.location.key ?? null;
    const saved = savedEntries();
    const found = saved.indexOf(key);
    if (found >= 0) { entryKeys = saved as (string | null)[]; entryIndex = found; }
    else { entryKeys = [key]; entryIndex = 0; }
    storeEntries();
    history.listen((location, action) => {
        const next = location.key ?? null;
        // The browser came back to this page after a refused Back or Forward: nothing moved.
        if (next === entryKeys[entryIndex]) return;
        if (action === 'PUSH') {
            entryKeys = [...entryKeys.slice(0, entryIndex + 1), next];
            entryIndex = entryKeys.length - 1;
        } else if (action === 'REPLACE') {
            entryKeys[entryIndex] = next;
        } else {
            const at = entryKeys.indexOf(next);
            if (at >= 0) entryIndex = at;
            else { entryKeys = [next]; entryIndex = 0; }
        }
        storeEntries();
    });
}
/** Rendered once inside the router, before any navigation. */
export function HistoryEntries() {
    const history = useHistory();
    React.useState(() => trackHistory(history));
    return null;
}
/** How far the browser's Back or Forward to ``to`` moves from ``from``; null when an entry was not seen. */
function historyDelta(from: Location, to: Location) {
    if (!trackedHistory) return null;
    const start = entryKeys.indexOf(from.key ?? null);
    const end = entryKeys.indexOf(to.key ?? null);
    return start < 0 || end < 0 || start === end ? null : end - start;
}
/** The router's key of the entry the browser is on (its history state), as the tracker stores keys. */
function browserKey() {
    const state: unknown = window.history.state;
    const key = state && typeof state === 'object' ? (state as { key?: unknown }).key : undefined;
    return typeof key === 'string' ? key : null;
}
/** After refusing a Back or Forward that moved the browser ``delta`` entries away from the page's entry ``pageKey``: the
 * history library moves the browser back by its own count (it calls history.go at once), which is right only when it
 * knows both entries; for entries from before a reload it may not move it, or move it the wrong distance. So the
 * browser is put on the page's entry: at once if the library did not move it, or once its move lands. */
function returnBrowser(delta: number, pageKey: string | null) {
    const before = goCalls;
    queueMicrotask(() => {
        if (goCalls === before) { window.history.go(-delta); return; }
        const settle = () => {
            window.removeEventListener('popstate', settle);
            window.clearTimeout(expiry);
            const at = entryKeys.indexOf(browserKey());
            const page = entryKeys.indexOf(pageKey);
            if (at >= 0 && page >= 0 && at !== page) window.history.go(page - at);
        };
        // The library's move lands in a moment; one that never lands (no such entry) leaves nothing to watch for.
        const expiry = window.setTimeout(() => window.removeEventListener('popstate', settle), 2000);
        window.addEventListener('popstate', settle);
    });
}

// Work under way inside the page that only one of its parts knows of (a recording in a student preview): the mounted
// builder guard lists it too, so that part needs no prompt of its own (the router keeps only one, and a second would
// replace the page's).
const leaveItems = new Map<string, string>();
let leaveItemsShown: string[] = [];
let guardsMounted = 0;
const leaveListeners = new Set<() => void>();
const subscribeLeave = (listener: () => void) => { leaveListeners.add(listener); return () => { leaveListeners.delete(listener); }; };
const leaveChanged = () => { leaveItemsShown = [...new Set(leaveItems.values())]; leaveListeners.forEach(listener => listener()); };
/** What the page's parts list now (useBuilderLeaveItem): work under way that a change of view or a Refresh stops. */
export function builderPartWork() {
    return leaveItemsShown;
}
/** Lists ``label`` in the builder page's leave dialog while ``active``; true when a builder page's guard is there to
 * list it (elsewhere, a student page, the caller asks with its own prompt). */
export function useBuilderLeaveItem(active: boolean, label: string) {
    const id = React.useId();
    const guarded = React.useSyncExternalStore(subscribeLeave, () => guardsMounted > 0);
    React.useEffect(() => {
        if (!active) return;
        leaveItems.set(id, label);
        leaveChanged();
        return () => { leaveItems.delete(id); leaveChanged(); };
    }, [active, label, id]);
    return guarded;
}

export interface LeaveGuardState {
    /** What leaving would lose, each said in the dialog (none: leaving asks only ``otherwise``). */
    unsaved: string[];
    /** Saves the page's unsaved edits (its Save all), resolving whether every one was saved; absent when leaving would
     * lose something Save all does not save (listed too), or while a request is under way. */
    save?: () => Promise<boolean>;
    /** Where the page says how its Save all went: shown when Save and continue did not save everything. */
    saveStatusId?: string;
    /** Leaving is refused outright (the page says why), such as while a picture request runs. */
    refuse?: () => boolean;
    /** With nothing unsaved: the question leaving asks (an open review's warning), or true to leave. */
    otherwise?: () => string | boolean;
}

/** The navigation waiting for the teacher's answer: a link's, answered through the router, or the browser's Back or
 * Forward, made again; ``question`` is a review's own question, asked instead of the unsaved list. */
interface Pending { answer?: (leave: boolean) => void; go?: { delta: number; key: string | undefined }; question?: string }

const buttonSx = { fontFamily: INTER, fontWeight: 700, textTransform: 'none', minHeight: 44 } as const;

export default function BuilderLeaveGuard({ when: pageWhen, state: pageState }: { when: boolean; state: LeaveGuardState }) {
    const history = useHistory();
    const id = React.useId();
    React.useEffect(() => { guardsMounted++; leaveChanged(); return () => { guardsMounted--; leaveChanged(); }; }, []);
    // Closing or reloading the tab warns for what the page's parts list, as each page's beforeunload does for its edits.
    React.useEffect(() => {
        const protect = (event: BeforeUnloadEvent) => { if (leaveItems.size) { event.preventDefault(); event.returnValue = ''; } };
        window.addEventListener('beforeunload', protect);
        return () => window.removeEventListener('beforeunload', protect);
    }, []);
    // What the page's parts list (useBuilderLeaveItem) is unsaved too; Save all does not save it.
    const partItems = React.useSyncExternalStore(subscribeLeave, () => leaveItemsShown);
    const state: LeaveGuardState = partItems.length
        ? { ...pageState, unsaved: [...pageState.unsaved, ...partItems], save: undefined } : pageState;
    const when = pageWhen || partItems.length > 0;
    const pendingRef = React.useRef<Pending | null>(null);
    const [pending, setPending] = React.useState<Pending | null>(null);
    const open = (next: Pending) => { pendingRef.current = next; setPending(next); };
    // The Back or Forward the teacher chose to make: let through once, to that entry (any other navigation clears it).
    const allowed = React.useRef<{ key: string | undefined } | null>(null);
    // The question to ask when the router asks this dialog (set by the Prompt just before).
    const nextQuestion = React.useRef<string | undefined>(undefined);
    const [saving, setSaving] = React.useState(false);
    const savingRef = React.useRef(false);
    const latest = React.useRef(state);
    latest.current = state;
    React.useEffect(() => {
        const ask = (answer: (leave: boolean) => void) => {
            if (pendingRef.current) { answer(false); return; }
            open({ answer, question: nextQuestion.current });
        };
        dialogs.set(id, ask);
        return () => { if (dialogs.get(id) === ask) dialogs.delete(id); };
    }, [id]);
    const respond = (leave: boolean) => {
        const current = pendingRef.current;
        pendingRef.current = null;
        setPending(null);
        if (!current) return;
        if (current.answer) current.answer(leave);
        else if (current.go && leave) {
            allowed.current = { key: current.go.key };
            history.go(current.go.delta);
        }
    };
    // What was listed finished while the dialog was open (a save completed, a request ended): leaving loses nothing
    // more, so the navigation goes ahead, or asks an open review's question instead.
    React.useEffect(() => {
        if (!pending || pending.question !== undefined || savingRef.current || state.unsaved.length) return;
        const rest = latest.current.otherwise?.() ?? true;
        if (typeof rest === 'string') open({ ...pending, question: rest });
        else respond(rest);
    });
    // A session that ended cannot save: its saves would be refused.
    const signedIn = pending ? isJwtLoggedIn() : true;
    const save = signedIn && pending?.question === undefined ? state.save : undefined;
    const saveAndContinue = async () => {
        const write = latest.current.save;
        if (!write) return;
        setSaving(true); savingRef.current = true;
        let saved = false;
        try {
            saved = await write();
        } catch {
            saved = false;
        } finally {
            setSaving(false); savingRef.current = false;
        }
        respond(saved);
        if (saved) return;
        // Not everything saved: the teacher stays, with the edits not saved and the page's message about them.
        const status = latest.current.saveStatusId;
        if (status) requestAnimationFrame(() => document.getElementById(status)?.scrollIntoView({ block: 'center' }));
    };
    const question = pending?.question;
    return <>
        <Prompt when={when} message={(location: Location, action: Action) => {
            if (allowed.current) {
                const chosen = allowed.current.key === location.key;
                allowed.current = null;
                if (chosen) return true;
            }
            // The browser returning to this page (see returnBrowser): nothing to ask.
            if (action === 'POP' && location.key === history.location.key) return true;
            const popDelta = action === 'POP' ? historyDelta(history.location, location) : null;
            // Already asking: the first question stands (the browser's Back pressed again returns here).
            if (pendingRef.current || latest.current.refuse?.()) {
                if (popDelta !== null) returnBrowser(popDelta, history.location.key ?? null);
                return false;
            }
            let asks: string | undefined;
            if (!latest.current.unsaved.length) {
                const rest = latest.current.otherwise?.() ?? true;
                if (typeof rest !== 'string') return rest;
                asks = rest;
            }
            if (popDelta !== null) {
                open({ go: { delta: popDelta, key: location.key }, question: asks });
                returnBrowser(popDelta, history.location.key ?? null);
                return false;
            }
            nextQuestion.current = asks;
            return DIALOG_MESSAGE + id;
        }} />
        <Dialog open={pending !== null} onClose={() => { if (!saving) respond(false); }} aria-labelledby={`${id}-title`} maxWidth="xs" fullWidth>
            <DialogTitle id={`${id}-title`} sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY }}>
                {question !== undefined ? 'Leave this page?' : 'Unsaved changes on this page'}
            </DialogTitle>
            <DialogContent>
                {question !== undefined ? <Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY }}>{question}</Typography> : <>
                    <Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY }}>Leaving this page now loses:</Typography>
                    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, fontFamily: INTER, fontSize: 14, color: NAVY }}>
                        {state.unsaved.map(item => <li key={item}>{item}</li>)}
                    </Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 1 }}>
                        {save ? 'Save and continue saves your edits first. If a save fails, you stay here with the edits not saved.'
                            : !signedIn ? 'Your session has ended, so nothing can be saved now. Stay here to copy your work, or discard it.'
                                : 'Stay here to finish or save them, or discard them.'}
                    </Typography>
                </>}
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
                <Button disabled={saving} onClick={() => respond(false)} sx={{ ...buttonSx, color: SLATE }}>Stay here</Button>
                <Button color="error" disabled={saving} onClick={() => respond(true)} sx={buttonSx}>
                    {question !== undefined ? 'Leave anyway' : 'Discard changes'}
                </Button>
                {save && <Button variant="contained" disabled={saving} onClick={() => void saveAndContinue()} sx={buttonSx}>
                    {saving ? 'Saving…' : 'Save and continue'}
                </Button>}
            </DialogActions>
        </Dialog>
    </>;
}
